import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicId } from "@/hooks/useAppointments";
import { subDays, subYears } from "date-fns";

export interface InactiveOwnerRow {
  ownerId: string;
  ownerName: string;
  lastAppointmentDate: Date;
  daysSinceLastVisit: number;
  totalHistoricalSpent: number;
  petCount: number;
  speciesNames: string[];
}

export function useReportsInactiveOwners(inactiveDays: number) {
  const { data: clinicId } = useCurrentClinicId();

  return useQuery({
    queryKey: ["reports-inactive-owners", clinicId, inactiveDays],
    queryFn: async (): Promise<InactiveOwnerRow[]> => {
      if (!clinicId) return [];

      const now = new Date();
      const cutoff = subDays(now, inactiveDays);
      const lookbackStart = subYears(now, 2);

      // Fetch all appointments in the last 2 years to calculate last visit per owner
      const { data: appointments, error: appErr } = await supabase
        .from("appointments")
        .select("owner_id, starts_at")
        .eq("clinic_id", clinicId)
        .gte("starts_at", lookbackStart.toISOString())
        .not("owner_id", "is", null)
        .not("status", "in", "(cancelado,faltou)");

      if (appErr) throw appErr;

      // Build last appointment date per owner
      const lastVisit: Record<string, Date> = {};
      (appointments ?? []).forEach((a) => {
        if (!a.owner_id) return;
        const d = new Date(a.starts_at);
        if (!lastVisit[a.owner_id] || d > lastVisit[a.owner_id]) {
          lastVisit[a.owner_id] = d;
        }
      });

      // Keep only owners whose last visit is before the cutoff
      const inactiveOwnerIds = Object.entries(lastVisit)
        .filter(([, last]) => last < cutoff)
        .map(([id]) => id);

      if (inactiveOwnerIds.length === 0) return [];

      // Fetch owner names
      const { data: owners, error: ownersErr } = await supabase
        .from("owners")
        .select("id, name")
        .in("id", inactiveOwnerIds);

      if (ownersErr) throw ownersErr;

      const ownerNameMap: Record<string, string> = {};
      (owners ?? []).forEach((o) => {
        ownerNameMap[o.id] = o.name ?? "Sem nome";
      });

      // Historical spend per owner
      const { data: sales } = await supabase
        .from("sales")
        .select("owner_id, total")
        .eq("clinic_id", clinicId)
        .in("owner_id", inactiveOwnerIds)
        .eq("status", "PAGA");

      const spendMap: Record<string, number> = {};
      (sales ?? []).forEach((s) => {
        if (!s.owner_id) return;
        spendMap[s.owner_id] = (spendMap[s.owner_id] ?? 0) + Number(s.total ?? 0);
      });

      // Pet count + species per owner
      const { data: pets } = await supabase
        .from("pets")
        .select("owner_id, species:species_id(name)")
        .in("owner_id", inactiveOwnerIds)
        .is("deleted_at", null);

      const petCountMap: Record<string, number> = {};
      const speciesMap: Record<string, Set<string>> = {};
      (pets ?? []).forEach((p: { owner_id: string; species: { name: string } | null }) => {
        petCountMap[p.owner_id] = (petCountMap[p.owner_id] ?? 0) + 1;
        if (p.species?.name) {
          if (!speciesMap[p.owner_id]) speciesMap[p.owner_id] = new Set();
          speciesMap[p.owner_id].add(p.species.name);
        }
      });

      const dayMs = 1000 * 60 * 60 * 24;

      return inactiveOwnerIds
        .filter((id) => ownerNameMap[id])
        .map((id) => ({
          ownerId: id,
          ownerName: ownerNameMap[id],
          lastAppointmentDate: lastVisit[id],
          daysSinceLastVisit: Math.floor((now.getTime() - lastVisit[id].getTime()) / dayMs),
          totalHistoricalSpent: spendMap[id] ?? 0,
          petCount: petCountMap[id] ?? 0,
          speciesNames: speciesMap[id] ? Array.from(speciesMap[id]) : [],
        }))
        .sort((a, b) => b.daysSinceLastVisit - a.daysSinceLastVisit);
    },
    enabled: !!clinicId,
  });
}
